import { env } from "./env";

type Environment = "headless" | "development" | "staging" | "prod" | "test" | "storybook";

export interface DatadogConfig {
  applicationId: string;
  clientToken: string;
  service: string;
  env: string;
}

export interface FeatureFlags {
  MODEL_BENCHMARKING: boolean;
  TEMPLATES: boolean;
  MODEL_RENTING: boolean;
  CHAT_KNOWLEDGE_BASE_MOCKS: boolean;
  GPU_MARKETPLACE_LISTING: boolean;
  MODEL_STATS: boolean;
  CHAT_MESSAGE_TAGS: boolean;
  NEURAL_PLUGIN_EXTRA_INFO: boolean;
  WORKFLOW: boolean;
  COPY_ID: boolean;
}

export interface ConfigType {
  domain: string;
  apiUrl: string;
  appUrl: string;
  chatUrl: string;
  datadog?: DatadogConfig;
  featureFlags: FeatureFlags;
}

export const CONFIG: Record<Environment, ConfigType> = {
  headless: {
    domain: "ala.local",
    apiUrl: "https://api.millenniumfalcon-ala.click",
    appUrl: "http://ala.local:3000",
    chatUrl: "http://chat.ala.local:3000",
    featureFlags: createFeatureFlags({}, false),
  },
  test: {
    domain: "localhost",
    apiUrl: "https://api.ala.local",
    appUrl: "http://localhost:3000",
    chatUrl: "http://localhost:3000/chat",
    featureFlags: createFeatureFlags({}, false),
  },
  development: {
    domain: "ala.local",
    apiUrl: "https://api.ala.local",
    appUrl: "https://ala.local",
    chatUrl: "https://chat.ala.local",
    featureFlags: createFeatureFlags({}, false),
  },
  staging: {
    domain: "millenniumfalcon-ala.click",
    apiUrl: "https://api.millenniumfalcon-ala.click",
    appUrl: "https://millenniumfalcon-ala.click",
    chatUrl: "https://chat.millenniumfalcon-ala.click",
    datadog: {
      applicationId: "d4416b11-866c-4a59-9962-2cd842743e53",
      clientToken: "pub69105dd55cb7c21a3808ecaf51c28d7c",
      service: "frontend",
      env: "dev",
    },
    featureFlags: createFeatureFlags({}, false),
  },
  prod: {
    domain: "flocplatform.com",
    apiUrl: "https://api.flocplatform.com",
    appUrl: "https://flocplatform.com",
    chatUrl: "https://chat.flocplatform.com",
    datadog: {
      applicationId: "362ac8fd-8405-43c9-ac75-16de80e6153a",
      clientToken: "pubefa800094f340272e9350e5429339af0",
      service: "frontend",
      env: "prod",
    },
    featureFlags: createFeatureFlags({}, false),
  },
  storybook: {
    domain: "localhost",
    apiUrl: "https://api.ala.local",
    appUrl: "http://localhost:6006",
    chatUrl: "http://localhost:6006/chat",
    featureFlags: createFeatureFlags({}, true),
  },
};

const determineEnvironment = (): Environment => {
  if (env.HEADLESS_MODE) {
    return "headless";
  }

  if (process.env.NEXT_PUBLIC_ALA_ENV) {
    return process.env.NEXT_PUBLIC_ALA_ENV as Environment;
  }

  return "development";
};

function createFeatureFlags(override: Partial<FeatureFlags>, defaultValue: true | false): FeatureFlags {
  return {
    MODEL_BENCHMARKING: defaultValue,
    TEMPLATES: defaultValue,
    MODEL_RENTING: defaultValue,
    CHAT_KNOWLEDGE_BASE_MOCKS: defaultValue,
    GPU_MARKETPLACE_LISTING: defaultValue,
    MODEL_STATS: defaultValue,
    CHAT_MESSAGE_TAGS: defaultValue,
    NEURAL_PLUGIN_EXTRA_INFO: defaultValue,
    WORKFLOW: defaultValue,
    COPY_ID: defaultValue,
    ...override,
  };
}

export const currentConfig = CONFIG[determineEnvironment()];
